import React, { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Container,
    Typography,
    Grid,
    Card,
    CardContent,
    TextField,
    InputAdornment,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Pagination,
    CircularProgress,
    Alert,
    Avatar,
    LinearProgress,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Snackbar,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Paper,
    Chip,
    Stack,
    Tabs,
    Tab,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import apiService from '../services/api';
import { getS3Url } from '../utils/imageUtils';
import { PodcastAnalysisSummary } from '../services/api';
import { useFavorites } from '../hooks/useFavorites';
import { useFilterStore } from '../stores/filterStore';
import { buildMonthOptions, buildWeekOptions } from '../utils/reviewDateFilterOptions';
import { initialSelectedWeekForReviewMode } from '../stores/filterStore';
import ReviewDayRangePicker from '../components/ReviewDayRangePicker';
import FavoriteButton from '../components/FavoriteButton';

type ReviewDigitalCategory = 'other' | 'politics' | 'tv' | 'noticias' | 'radio';

function podcastMatchesFavoriteCompanies(
    p: PodcastAnalysisSummary,
    favoriteIds: Set<string>,
): boolean {
    if (favoriteIds.size === 0) return false;
    if (p.companyIds?.some((id) => favoriteIds.has(id))) return true;
    if (p.companyId && favoriteIds.has(p.companyId)) return true;
    return false;
}

const PodcastAnalysisReview: React.FC = () => {
    const navigate = useNavigate();
    const { getFavoriteIdsByType } = useFavorites();
    const favoriteCompanyIds = getFavoriteIdsByType('company');
    const favoriteCompanyIdSet = React.useMemo(
        () => new Set(favoriteCompanyIds),
        [favoriteCompanyIds],
    );

    // State for filters and pagination (Podcasts tab)
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('uniquePaidBrands');
    const [page, setPage] = useState(1);
    const [reviewCategory, setReviewCategory] = useState<ReviewDigitalCategory>('other');

    const {
        selectedWeek,
        setSelectedWeek,
        reviewDateFilterMode,
        setReviewDateFilterMode,
    } = useFilterStore();

    const weekOptions = React.useMemo(() => buildWeekOptions(), []);
    const monthOptions = React.useMemo(() => buildMonthOptions(), []);

    // State for table sorting
    const [podcastOrder, setPodcastOrder] = useState<'asc' | 'desc'>('desc');
    const [podcastOrderBy, setPodcastOrderBy] = useState('uniquePaidBrands');

    // Removed sort keys: normalize so API and UI stay in sync
    React.useEffect(() => {
        if (sortBy === 'totalViews' || sortBy === 'avgViewsPerContent') {
            setSortBy('uniquePaidBrands');
            setPodcastOrderBy('uniquePaidBrands');
            setPodcastOrder('desc');
        }
        if (sortBy === 'paidMentions') {
            setSortBy('brandMentionsCount');
            setPodcastOrderBy('brandMentionsCount');
            setPodcastOrder('desc');
        }
    }, [sortBy]);

    // State for delete functionality
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedPodcastForDelete, setSelectedPodcastForDelete] = useState<PodcastAnalysisSummary | null>(null);
    const [currentMonth] = useState(() => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    });
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
    const [isDeleting, setIsDeleting] = useState(false);

    // Query for podcast analysis summary
    const {
        data: podcastsData,
        isLoading: loading,
        error: podcastsError,
    } = useQuery({
        queryKey: [
            'podcast-analysis-summary',
            { searchTerm, sortBy, selectedWeek, reviewDateFilterMode, page, reviewCategory },
        ],
        queryFn: () =>
            apiService.getPodcastAnalysisSummary(
                page,
                100,
                searchTerm,
                '',
                '',
                '',
                sortBy,
                undefined,
                selectedWeek !== 'all' ? selectedWeek : undefined,
                reviewCategory,
            ),
        staleTime: 0, // 2 minutes
        gcTime: 0,
        enabled: true,
        placeholderData: keepPreviousData,
    });

    // Backend handles week filtering and sorting (including secondary sort by avgViewsPerContent)
    const podcasts = React.useMemo(() => podcastsData?.podcasts || [], [podcastsData?.podcasts]);

    const { companyFavoritePodcasts, restPodcasts } = React.useMemo(() => {
        if (favoriteCompanyIdSet.size === 0) {
            return {
                companyFavoritePodcasts: [] as PodcastAnalysisSummary[],
                restPodcasts: podcasts,
            };
        }
        const companyFavorites: PodcastAnalysisSummary[] = [];
        const rest: PodcastAnalysisSummary[] = [];
        for (const p of podcasts) {
            if (podcastMatchesFavoriteCompanies(p, favoriteCompanyIdSet)) {
                companyFavorites.push(p);
            } else {
                rest.push(p);
            }
        }
        return { companyFavoritePodcasts: companyFavorites, restPodcasts: rest };
    }, [podcasts, favoriteCompanyIdSet]);

    const totalPages = podcastsData?.pagination?.pages || 1;
    const totals = podcastsData?.totals || {
        totalPodcasts: 0,
        totalContent: 0,
        totalTranscribed: 0,
        totalAnalyzed: 0,
        totalReady: 0,
        totalBrandMentions: 0,
        totalPaidMentions: 0,
        totalUniqueBrands: 0,
        totalUniquePaidBrands: 0,
        totalAcceptedPaidBrands: 0,
        podcastsWithPaidBrands: 0,
        contentWithPaidMentions: 0,
        medianSponsorsPerPodcast: 0,
        totalDuration: 0
    };
    const totalReady = totals.totalReady || 0;
    const analyzedEpisodes = totals.totalAnalyzed || 0;
    // Ready is counted only on analyzed episodes, across every channel type.
    const reviewProgress =
        analyzedEpisodes > 0
            ? Math.round((totalReady / analyzedEpisodes) * 100)
            : 0;
    const error = podcastsError ? 'Failed to load podcast analysis data' : null;

    // Helper function to format duration
    const formatDuration = (seconds: number): string => {
        if (!seconds || seconds === 0) return '0h';
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        if (hours > 0) {
            return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
        }
        return `${minutes}m`;
    };

    const handlePodcastSelect = async (podcast: PodcastAnalysisSummary, event?: React.MouseEvent) => {
        // Check if Cmd (Mac) or Ctrl (Windows/Linux) was pressed, or if it's a middle click
        if (event && (event.metaKey || event.ctrlKey || event.button === 1)) {
            // Open in new tab
            window.open(`/review/${podcast._id}`, '_blank');
        } else {
            // Normal navigation
            navigate(`/review/${podcast._id}`);
        }
    };

    // Delete action column removed from table; handler kept for potential future use (e.g. context menu)
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const handleDeleteClick = (podcast: PodcastAnalysisSummary, event: React.MouseEvent) => {
        event.stopPropagation(); // Prevent card click
        setSelectedPodcastForDelete(podcast);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!selectedPodcastForDelete) return;

        setIsDeleting(true);
        try {
            const result = await apiService.deleteAllPodcastMonthMentions(
                selectedPodcastForDelete._id,
                currentMonth
            );

            setSnackbarMessage(
                `Successfully deleted ${result.deletedMentions} mentions from ${result.affectedContent} episodes for ${currentMonth}`
            );
            setSnackbarSeverity('success');
            setSnackbarOpen(true);

            // Refresh the data
            window.location.reload();
        } catch (error: any) {
            console.error('Error deleting mentions:', error);
            setSnackbarMessage(
                error.response?.data?.error || 'Failed to delete mentions. Please try again.'
            );
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        } finally {
            setIsDeleting(false);
            setDeleteDialogOpen(false);
            setSelectedPodcastForDelete(null);
        }
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setSelectedPodcastForDelete(null);
    };

    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
    };

    const getAnalysisStatusColor = (progress: number) => {
        if (progress === 100) return 'success';
        if (progress > 0) return 'warning';
        return 'error';
    };

    const getReadyProgress = (podcast: PodcastAnalysisSummary) =>
        podcast.readyProgress ??
        (podcast.totalContent > 0
            ? Math.round(((podcast.readyContent || 0) / podcast.totalContent) * 100)
            : 0);

    // Podcast table sort handlers
    const handlePodcastRequestSort = (property: string) => {
        const isAsc = podcastOrderBy === property && podcastOrder === 'asc';
        setPodcastOrder(isAsc ? 'desc' : 'asc');
        setPodcastOrderBy(property);

        // Update the main sortBy state to sync with API
        setSortBy(property);
        setPage(1);
    };

    const renderPodcastTableHead = () => (
        <TableHead>
            <TableRow>
                <TableCell align="center" sx={{ width: 48 }} aria-label="Favorito" />
                <TableCell align="center" sx={{ width: 60 }}>
                    #
                </TableCell>
                <TableCell>
                    <TableSortLabel
                        active={podcastOrderBy === 'name'}
                        direction={podcastOrderBy === 'name' ? podcastOrder : 'asc'}
                        onClick={() => handlePodcastRequestSort('name')}
                    >
                        Canal
                    </TableSortLabel>
                </TableCell>
                <TableCell align="center">
                    <TableSortLabel
                        active={podcastOrderBy === 'uniquePaidBrands'}
                        direction={podcastOrderBy === 'uniquePaidBrands' ? podcastOrder : 'asc'}
                        onClick={() => handlePodcastRequestSort('uniquePaidBrands')}
                    >
                        Unique Paid Brands
                    </TableSortLabel>
                </TableCell>
                <TableCell align="center">
                    <TableSortLabel
                        active={podcastOrderBy === 'analysisProgress'}
                        direction={podcastOrderBy === 'analysisProgress' ? podcastOrder : 'asc'}
                        onClick={() => handlePodcastRequestSort('analysisProgress')}
                    >
                        Progress
                    </TableSortLabel>
                </TableCell>
                <TableCell align="center">
                    <TableSortLabel
                        active={podcastOrderBy === 'totalContent'}
                        direction={podcastOrderBy === 'totalContent' ? podcastOrder : 'asc'}
                        onClick={() => handlePodcastRequestSort('totalContent')}
                    >
                        Content
                    </TableSortLabel>
                </TableCell>
                <TableCell align="center">
                    <TableSortLabel
                        active={podcastOrderBy === 'readyProgress'}
                        direction={podcastOrderBy === 'readyProgress' ? podcastOrder : 'asc'}
                        onClick={() => handlePodcastRequestSort('readyProgress')}
                    >
                        Ready Progress
                    </TableSortLabel>
                </TableCell>
                <TableCell align="center">
                    <TableSortLabel
                        active={podcastOrderBy === 'transcribedContent'}
                        direction={podcastOrderBy === 'transcribedContent' ? podcastOrder : 'asc'}
                        onClick={() => handlePodcastRequestSort('transcribedContent')}
                    >
                        Transcribed
                    </TableSortLabel>
                </TableCell>
                <TableCell align="center">
                    <TableSortLabel
                        active={podcastOrderBy === 'brandMentionsCount'}
                        direction={podcastOrderBy === 'brandMentionsCount' ? podcastOrder : 'asc'}
                        onClick={() => handlePodcastRequestSort('brandMentionsCount')}
                    >
                        Brand Mentions
                    </TableSortLabel>
                </TableCell>
            </TableRow>
        </TableHead>
    );

    const renderPodcastRow = (displayIndex: number, podcast: PodcastAnalysisSummary) => (
        <TableRow
            key={podcast._id}
            hover
            sx={{ cursor: 'pointer' }}
            onClick={(e) => handlePodcastSelect(podcast, e)}
        >
            <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                <FavoriteButton entityType="podcast" entityId={podcast._id} />
            </TableCell>
            <TableCell align="center">
                <Typography variant="body2" color="text.secondary">
                    {displayIndex}
                </Typography>
            </TableCell>
            <TableCell>
                <Box display="flex" alignItems="center" gap={2}>
                    <Avatar
                        src={podcast.imageS3Key ? getS3Url(podcast.imageS3Key) : podcast.imageUrl}
                        sx={{ width: 40, height: 40 }}
                    />
                    <Box>
                        <Typography variant="subtitle2" noWrap sx={{ maxWidth: 200 }}>
                            {podcast.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {podcast.author}
                        </Typography>
                    </Box>
                </Box>
            </TableCell>
            <TableCell align="center">
                <Typography variant="body2">
                    {podcast.uniquePaidBrands || 0}
                </Typography>
            </TableCell>
            <TableCell align="center">
                <Box display="flex" alignItems="center" gap={1}>
                    <LinearProgress
                        variant="determinate"
                        value={podcast.analysisProgress || 0}
                        color={getAnalysisStatusColor(podcast.analysisProgress || 0)}
                        sx={{ width: 60, height: 6, borderRadius: 3 }}
                    />
                    <Typography variant="caption">
                        {podcast.analysisProgress || 0}%
                    </Typography>
                </Box>
                {(podcast.pendingMentionsCount || 0) > 0 && (
                    <Typography
                        variant="caption"
                        color="warning.main"
                        display="block"
                        textAlign="left"
                        noWrap
                    >
                        {podcast.pendingMentionsCount} pending mention
                        {podcast.pendingMentionsCount === 1 ? '' : 's'} ·{' '}
                        {podcast.pendingMentionsContent} ep
                        {podcast.pendingMentionsContent === 1 ? '' : 's'}
                    </Typography>
                )}
            </TableCell>
            <TableCell align="center">
                <Typography variant="body2">
                    {podcast.analyzedContent || 0}/{podcast.totalContent || 0}
                </Typography>
            </TableCell>
            <TableCell align="center">
                <Box display="flex" alignItems="center" gap={1}>
                    <LinearProgress
                        variant="determinate"
                        value={getReadyProgress(podcast)}
                        color={getAnalysisStatusColor(getReadyProgress(podcast))}
                        sx={{ width: 60, height: 6, borderRadius: 3 }}
                    />
                    <Typography variant="caption">
                        {getReadyProgress(podcast)}%
                    </Typography>
                </Box>
            </TableCell>
            <TableCell align="center">
                <Typography variant="body2">
                    {podcast.transcribedContent || 0}
                </Typography>
            </TableCell>
            <TableCell align="center">
                <Chip
                    label={podcast.brandMentionsCount || 0}
                    size="small"
                    color={podcast.brandMentionsCount > 0 ? "success" : "default"}
                />
            </TableCell>
        </TableRow>
    );

    const showFavoriteCompaniesTable =
        favoriteCompanyIdSet.size > 0 && companyFavoritePodcasts.length > 0;

    // Remove the full-page loading state - we'll show skeletons in the grid instead

    return (
        <Container
            maxWidth="xl"
            sx={{
                mt: 4,
                mb: 4,
                '& .MuiTypography-root': {
                    fontFamily: (theme) => theme.typography.fontFamily,
                },
            }}
        >
            <Box mb={4}>
                <Typography variant="h1" gutterBottom>
                    Review
                </Typography>
            </Box>

            {/* Filters & Search - Now applies to both tabs */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        Filters & Search
                    </Typography>
                    <Stack
                        direction="row"
                        spacing={2}
                        alignItems="center"
                        useFlexGap
                        sx={{
                            flexWrap: 'nowrap',
                            overflowX: 'auto',
                            pb: 0.5,
                        }}
                    >
                        <Box sx={{ flex: '1 1 auto', minWidth: 220 }}>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="Search podcasts..."
                                value={searchTerm}
                                onChange={(e) => {
                                    setSearchTerm(e.target.value);
                                    setPage(1);
                                }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Box>
                        <Box sx={{ flex: '0 0 auto', minWidth: 160 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Sort By</InputLabel>
                                <Select
                                    value={(() => {
                                        const validPodcastSorts = [
                                            'name',
                                            'brandMentionsCount',
                                            'uniquePaidBrands',
                                            'analysisProgress',
                                            'totalContent',
                                            'readyProgress',
                                            'readyContent',
                                            'transcribedContent',
                                        ];
                                        return validPodcastSorts.includes(sortBy)
                                            ? sortBy
                                            : 'uniquePaidBrands';
                                    })()}
                                    label="Sort By"
                                    onChange={(e) => {
                                        setSortBy(e.target.value);
                                        setPodcastOrderBy(e.target.value);
                                        setPage(1);
                                    }}
                                >
                                    <MenuItem value="name">Name</MenuItem>
                                    <MenuItem value="brandMentionsCount">Brand Mentions</MenuItem>
                                    <MenuItem value="uniquePaidBrands">Unique Paid Brands</MenuItem>
                                    <MenuItem value="analysisProgress">Progress</MenuItem>
                                    <MenuItem value="totalContent">Total Content</MenuItem>
                                    <MenuItem value="readyProgress">Ready Progress</MenuItem>
                                    <MenuItem value="transcribedContent">Transcribed</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                        <Box sx={{ flex: '0 0 auto', minWidth: 130 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Date Filter</InputLabel>
                                <Select
                                    value={reviewDateFilterMode}
                                    label="Date Filter"
                                    onChange={(e) => {
                                        const newMode = e.target.value as 'week' | 'month' | 'day';
                                        setReviewDateFilterMode(newMode);
                                        setSelectedWeek(initialSelectedWeekForReviewMode(newMode));
                                        setPage(1);
                                    }}
                                >
                                    <MenuItem value="week">By Week</MenuItem>
                                    <MenuItem value="month">By Month</MenuItem>
                                    <MenuItem value="day">By Day</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                        <Box sx={{ flex: '0 0 auto', minWidth: 220 }}>
                            {reviewDateFilterMode === 'day' ? (
                                <ReviewDayRangePicker
                                    value={selectedWeek}
                                    onChange={(value) => {
                                        setSelectedWeek(value);
                                        setPage(1);
                                    }}
                                />
                            ) : (
                            <FormControl fullWidth size="small">
                                <InputLabel>{reviewDateFilterMode === 'week' ? 'Week' : 'Month'}</InputLabel>
                                <Select
                                    value={selectedWeek}
                                    label={reviewDateFilterMode === 'week' ? 'Week' : 'Month'}
                                    onChange={(e) => {
                                        setSelectedWeek(e.target.value);
                                        setPage(1);
                                    }}
                                >
                                    {(reviewDateFilterMode === 'week' ? weekOptions : monthOptions).map(option => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            )}
                        </Box>
                    </Stack>
                </CardContent>
            </Card>

            {/* Summary cards: analyzed episodes from every channel type.
                Total Content and Analysis Progress still use the full catalog
                so the progress ratio is analyzed ÷ all episodes. */}
                {/* First Row - 6 cards */}
                <Grid container spacing={3} sx={{ mb: 3 }}>
                    <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" color="secondary">
                                    {totals.totalPodcasts || 0}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Podcasts with Content
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" color="primary">
                                    {totals.totalContent || 0}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Total Content
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" color="info.main">
                                    {totals.totalAnalyzed || 0}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Analyzed
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" color="primary">
                                    {formatDuration(totals.totalDuration || 0)}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Total Duration
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" color="success.main">
                                    {(totals as any).podcastsWithPaidBrands || 0}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    With Paid Brands
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" color="warning.main">
                                    {Math.round((totals.totalAnalyzed / (totals.totalContent || 1)) * 100)}%
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Analysis Progress
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* Review Progress — ready / analyzed, all channel types */}
                <Grid container spacing={3} sx={{ mb: 3 }}>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <Card>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1 }}>
                                    <Typography variant="h6" color={`${getAnalysisStatusColor(reviewProgress)}.main`}>
                                        {totalReady} / {analyzedEpisodes}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        ({reviewProgress}%)
                                    </Typography>
                                </Box>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                    Review Progress
                                </Typography>
                                <LinearProgress
                                    variant="determinate"
                                    value={reviewProgress}
                                    color={getAnalysisStatusColor(reviewProgress)}
                                    sx={{ height: 8, borderRadius: 1 }}
                                />
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* Second Row — mentions on analyzed episodes, every channel type */}
                <Grid container spacing={3} sx={{ mb: 4 }}>
                    <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" color="warning.main">
                                    {totals.totalBrandMentions || 0}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Brand Mentions
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" color="success.main">
                                    {totals.contentWithPaidMentions || 0}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Content with Paid Mentions
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" color="info.main">
                                    {totals.totalUniqueBrands || 0}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Unique Brands
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" color="error.main">
                                    {totals.totalUniquePaidBrands || 0}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Unique Paid Brands
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" color="success.main">
                                    {totals.totalAcceptedPaidBrands || 0}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Accepted Brands
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>


                <Box display="flex" justifyContent="flex-end" mb={2}>
                    <Tabs
                        value={reviewCategory}
                        onChange={(_, value: ReviewDigitalCategory) => {
                            setReviewCategory(value);
                            setPage(1);
                        }}
                        textColor="primary"
                        indicatorColor="primary"
                    >
                        <Tab value="other" label="Otros" />
                        <Tab value="politics" label="Política" />
                        <Tab value="tv" label="TV" />
                        <Tab value="noticias" label="Noticias" />
                        <Tab value="radio" label="Radio" />
                    </Tabs>
                </Box>

                {/* Results */}
                {error ? (
                    <Alert severity="error">{error}</Alert>
                ) : (
                    <>
                        {showFavoriteCompaniesTable && (
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="h6" gutterBottom>
                                    Podcasts de empresas favoritas
                                </Typography>
                                <TableContainer component={Paper}>
                                    <Table size="small">
                                        {renderPodcastTableHead()}
                                        <TableBody>
                                            {companyFavoritePodcasts.map((podcast, index) =>
                                                renderPodcastRow(index + 1, podcast),
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Box>
                        )}

                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                flexWrap: 'wrap',
                                gap: 1,
                                mb: 2,
                            }}
                        >
                            <Typography variant="h6">
                                Todos los podcasts
                            </Typography>
                        </Box>

                        <TableContainer component={Paper}>
                            <Table>
                                {renderPodcastTableHead()}
                                <TableBody>
                                    {loading && podcasts.length === 0 ? (
                                        Array.from({ length: 10 }).map((_, index) => (
                                            <TableRow key={`skeleton-${index}`}>
                                                <TableCell align="center">
                                                    <Skeleton variant="circular" width={32} height={32} />
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Skeleton variant="text" width={30} />
                                                </TableCell>
                                                <TableCell>
                                                    <Box display="flex" alignItems="center" gap={2}>
                                                        <Skeleton variant="circular" width={40} height={40} />
                                                        <Box>
                                                            <Skeleton variant="text" width={200} />
                                                            <Skeleton variant="text" width={150} />
                                                        </Box>
                                                    </Box>
                                                </TableCell>
                                                <TableCell><Skeleton variant="text" width={80} /></TableCell>
                                                <TableCell><Skeleton variant="text" width={80} /></TableCell>
                                                <TableCell><Skeleton variant="text" width={60} /></TableCell>
                                                <TableCell><Skeleton variant="text" width={60} /></TableCell>
                                                <TableCell><Skeleton variant="text" width={60} /></TableCell>
                                                <TableCell><Skeleton variant="text" width={60} /></TableCell>
                                            </TableRow>
                                        ))
                                    ) : restPodcasts.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={9} align="center">
                                                <Typography variant="body2" color="text.secondary">
                                                    {showFavoriteCompaniesTable
                                                        ? 'No hay más podcasts en esta vista'
                                                        : 'No hay podcasts'}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        restPodcasts.map((podcast, index) =>
                                            renderPodcastRow(index + 1, podcast),
                                        )
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {totalPages > 1 && (
                            <Box display="flex" justifyContent="center" mt={4}>
                                <Pagination
                                    count={totalPages}
                                    page={page}
                                    onChange={(_, newPage) => setPage(newPage)}
                                    color="primary"
                                />
                            </Box>
                        )}
                    </>
                )}

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={handleDeleteCancel}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    Delete All Mentions for {currentMonth}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete all brand mentions for{' '}
                        <strong>{selectedPodcastForDelete?.title}</strong> in{' '}
                        <strong>{currentMonth}</strong>?
                        <br /><br />
                        This action will:
                        <br />• Delete all brand mentions from episodes published in {currentMonth}
                        <br />• Reset the analysis status for those episodes
                        <br />• Remove all analysis run records for that month
                        <br /><br />
                        <strong>This action cannot be undone.</strong>
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteCancel} disabled={isDeleting}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDeleteConfirm}
                        color="error"
                        variant="contained"
                        disabled={isDeleting}
                    >
                        {isDeleting ? <CircularProgress size={20} /> : 'Delete All Mentions'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Success/Error Snackbar */}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleSnackbarClose}
                    severity={snackbarSeverity}
                    sx={{ width: '100%' }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default PodcastAnalysisReview;